import { Outlet, useLocation, Link } from 'react-router-dom';
import { useActor, ACTORS } from '@/context/ActorContext';
import { cn } from '@/lib/utils';
import { ENTITY_LOGOS } from '@/lib/assets';
import { 
  LayoutDashboard, Settings, ClipboardCheck, DollarSign, School, FlaskConical, 
  GraduationCap, BarChart3, BookOpen, Send, Upload, FileText, Truck, Receipt,
  Wallet, Package, ShoppingCart, MessageSquare, History, Bell, ChevronDown,
  LogOut, Menu, X
} from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const iconMap = {
  LayoutDashboard, Settings, ClipboardCheck, DollarSign, School, FlaskConical,
  GraduationCap, BarChart3, BookOpen, Send, Upload, FileText, Truck, Receipt,
  Wallet, Package, ShoppingCart, MessageSquare, History
};

const logoKeyMap = {
  moe_hq: 'moe',
  civica: 'civica',
  school: 'rgs',
  supplier: 'opentrolley',
  mk: 'mk'
};

export default function DashboardLayout() {
  const { currentActor, switchActor, navigation } = useActor();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const logoUrl = ENTITY_LOGOS[logoKeyMap[currentActor.id]];

  const handleActorSwitch = (actorId) => {
    switchActor(actorId);
    window.location.href = ACTORS[actorId].basePath;
  };

  return (
    <div className="min-h-screen bg-[#F0F2F7]">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-[#1A3C6E] text-white z-40 transition-transform duration-300",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo and Actor Info */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 flex items-center justify-center">
                <img 
                  src={logoUrl} 
                  alt={currentActor.shortName}
                  className="w-full h-full object-contain p-1"
                  style={{ filter: currentActor.id === 'moe_hq' || currentActor.id === 'mk' ? 'none' : 'brightness(0) invert(1)' }}
                />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-sm leading-tight">
                  {currentActor.shortName}
                </h2>
                <p className="text-xs text-white/60">{currentActor.role}</p>
              </div>
            </div>
            <div className="text-xs text-white/50">
              SLF CDS Acquisition Module
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navigation.map((item) => {
                const Icon = iconMap[item.icon] || LayoutDashboard;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "sidebar-link",
                      isActive ? "sidebar-link-active" : "sidebar-link-inactive"
                    )}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Actor Switcher */}
          <div className="p-4 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-between text-white/80 hover:text-white hover:bg-white/10"
                  data-testid="actor-switcher"
                >
                  <span className="text-xs">Switch Portal</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Select Portal</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {Object.values(ACTORS).map((actor) => (
                  <DropdownMenuItem 
                    key={actor.id}
                    onClick={() => handleActorSwitch(actor.id)}
                    className="cursor-pointer"
                    data-testid={`switch-to-${actor.id}`}
                  >
                    <div 
                      className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white mr-2"
                      style={{ backgroundColor: actor.color }}
                    >
                      {actor.logoInitials}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{actor.shortName}</div>
                      <div className="text-xs text-muted-foreground">{actor.role}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <div className="lg:hidden w-8" /> {/* Spacer for mobile menu button */}
              <div>
                <h1 className="font-heading font-semibold text-slate-900">
                  {currentActor.name}
                </h1>
                <p className="text-xs text-slate-500">CDS Cycle 1 2028 — Selection Phase</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5 text-slate-600" />
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-[10px]">
                  4
                </Badge>
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-xs font-medium text-slate-600">
                        {currentActor.userName.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div className="hidden md:block text-left">
                      <div className="text-sm font-medium text-slate-900">{currentActor.userName}</div>
                      <div className="text-xs text-slate-500">{currentActor.shortName}</div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-normal">
                      <div className="font-medium">{currentActor.userName}</div>
                      <div className="text-xs text-muted-foreground">{currentActor.userEmail}</div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/" className="flex items-center text-red-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      Switch Actor
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
